'use client';

import { useCallback } from 'react';
import { posthog } from '@web/lib/analytics/posthog-client';

/**
 * Custom hook for tracking application analytics events.
 *
 * PostHog is the only browser sink (GAR-1133). Staff activity that has to be
 * auditable is emitted server-side into the Postgres activity log — never
 * from best-effort browser capture.
 *
 * Every event already carries `tenant_id` / `organization_id` / `team_id` as
 * PostHog super-properties (registered once in `AnalyticsProvider` on
 * identify) and is attributed to the user via `distinct_id` — do not
 * re-stamp camelCase copies (`tenantId`/`userId`/`organizationId`/`teamId`)
 * here. The audit found every client event carrying both spellings; the
 * documented convention is snake_case (gar-6rj.5).
 */
export function useAppAnalytics() {
  /**
 * Generic event tracking function
 */
  const trackEvent = useCallback(
    (eventName: string, properties?: Record<string, unknown>) => {
      const enrichedProperties = {
        ...properties,
        timestamp: new Date().toISOString(),
      };

      if (typeof window !== 'undefined' && posthog) {
        try {
          posthog.capture(eventName, enrichedProperties);
        } catch (_error) {
          // Silently skip if analytics not ready
        }
      }
    },
    []
  );

  /**
 * Track property-related events
 */
  const trackPropertyEvent = useCallback(
    (action: string, propertyId: string, properties?: Record<string, unknown>) => {
      trackEvent(`property_${action}`, {
        propertyId,
        ...properties,
      });
    },
    [trackEvent]
  );

  /**
 * Track contact view event (replacing the mutation)
 */
  const trackContactView = useCallback(
    (propertyId: string, contactIndex: number, contactName: string) => {
      trackEvent('contact_viewed', {
        propertyId,
        contactIndex,
        contactName,
        viewContext: 'property_details',
      });
    },
    [trackEvent]
  );

  /**
 * Track deal/CRM events
 */
  const trackDealEvent = useCallback(
    (action: string, dealId: string, properties?: Record<string, unknown>) => {
      trackEvent(`deal_${action}`, {
        dealId,
        ...properties,
      });
    },
    [trackEvent]
  );

  /**
 * Track task events
 */
  const trackTaskEvent = useCallback(
    (action: string, taskId: string, properties?: Record<string, unknown>) => {
      trackEvent(`task_${action}`, {
        taskId,
        ...properties,
      });
    },
    [trackEvent]
  );

  /**
 * Track search events
 */
  const trackSearch = useCallback(
    (searchType: string, query: string, resultCount?: number) => {
      trackEvent('search_performed', {
        searchType,
        query,
        resultCount,
      });
    },
    [trackEvent]
  );

  /**
 * Track form submissions
 */
  const trackFormSubmit = useCallback(
    (
      formName: string,
      success: boolean,
      errorMessage?: string,
      extra?: Record<string, unknown>
    ) => {
      trackEvent('form_submitted', {
        formName,
        success,
        ...(errorMessage != null && errorMessage !== ''
          ? { errorMessage }
          : {}),
        ...extra,
      });
    },
    [trackEvent]
  );

  /**
 * Track document events
 */
  const trackDocumentEvent = useCallback(
    (action: string, documentType: string, documentId?: string, properties?: Record<string, unknown>) => {
      trackEvent(`document_${action}`, {
        documentType,
        documentId,
        ...properties,
      });
    },
    [trackEvent]
  );

  /**
 * Track listing events
 */
  const trackListingEvent = useCallback(
    (action: string, listingId: string, properties?: Record<string, unknown>) => {
      trackEvent(`listing_${action}`, {
        listingId,
        ...properties,
      });
    },
    [trackEvent]
  );

  /**
 * Track transaction events
 */
  const trackTransactionEvent = useCallback(
    (action: string, transactionId: string, properties?: Record<string, unknown>) => {
      trackEvent(`transaction_${action}`, {
        transactionId,
        ...properties,
      });
    },
    [trackEvent]
  );

  /**
 * Track messaging events
 */
  const trackMessagingEvent = useCallback(
    (action: string, properties?: Record<string, unknown>) => {
      trackEvent(`messaging_${action}`, {
        ...properties,
      });
    },
    [trackEvent]
  );

  /**
 * Track tab navigation
 */
  const trackTabNavigation = useCallback(
    (tabName: string, context: string) => {
      trackEvent('tab_navigated', {
        tabName,
        context,
      });
    },
    [trackEvent]
  );

  /**
 * Track filter changes
 */
  const trackFilterChange = useCallback(
    (filterType: string, filterValue: unknown, context: string) => {
      trackEvent('filter_changed', {
        filterType,
        filterValue,
        context,
      });
    },
    [trackEvent]
  );

  /**
 * Track modal/drawer events
 */
  const trackModalEvent = useCallback(
    (action: 'opened' | 'closed', modalName: string, context?: string) => {
      trackEvent(`modal_${action}`, {
        modalName,
        context,
      });
    },
    [trackEvent]
  );

  /**
 * Track file upload events
 */
  const trackFileUpload = useCallback(
    (fileType: string, fileSize: number, success: boolean, context?: string) => {
      trackEvent('file_uploaded', {
        fileType,
        fileSize,
        success,
        context,
      });
    },
    [trackEvent]
  );

  /**
 * Track export events
 */
  const trackExport = useCallback(
    (exportType: string, format: string, recordCount?: number) => {
      trackEvent('data_exported', {
        exportType,
        format,
        recordCount,
      });
    },
    [trackEvent]
  );

  /**
 * Track error events (sent to PostHog as error_occurred for exception analysis)
 * @param errorType - e.g. 'property_validate_address_failed'
 * @param errorMessage - user-facing or technical message
 * @param context - optional context string (e.g. 'localizacao_tab')
 * @param extra - optional props for PostHog filtering (flowStep, mode, etc.)
 */
  const trackError = useCallback(
    (
      errorType: string,
      errorMessage: string,
      context?: string,
      extra?: Record<string, unknown>
    ) => {
      trackEvent('error_occurred', {
        errorType,
        errorMessage,
        ...(context != null && { context }),
        ...extra,
      });
    },
    [trackEvent]
  );

  return {
    // Core tracking
    trackEvent,
    // NOTE: no trackPageView here — `$pageview` is captured exactly once per
    // navigation by `<RouteAnalytics />` (lib/analytics/page-tracking.ts).
    // A second manual `$pageview` used to be fired from here via
    // `page-tracker.tsx`, double-counting every navigation (gar-6rj.5).

    // Feature-specific tracking
    trackPropertyEvent,
    trackContactView,
    trackDealEvent,
    trackTaskEvent,
    trackSearch,
    trackFormSubmit,
    trackDocumentEvent,
    trackListingEvent,
    trackTransactionEvent,
    trackMessagingEvent,
    
    // UI interaction tracking
    trackTabNavigation,
    trackFilterChange,
    trackModalEvent,
    trackFileUpload,
    trackExport,
    
    // Error tracking
    trackError,
    
    // Raw analytics object for advanced usage
    posthogAnalytics: typeof window !== 'undefined' ? posthog : null,
  };
}
